import {doQuerySafe, doQueryWithoutCache} from 'graphql-client/ApolloWrappers';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {resolveDraftPropertyFromSmiles} from 'notebook/integrations/chemistry/custom-evals/DraftChemicalPropertiesCustomEval';
import {getVersionedBioentityFromMoleculeVersionCell} from 'notebook/integrations/chemistry/custom-evals/MoleculeVersionCustomEval';
import type {StoichiometryCustomEvalOptions} from 'notebook/integrations/chemistry/StoichiometryStructuredTableAdapter';
import makeCustomResolutionResultFromTypedValue from 'notebook/integrations/samples/transfer-table-columns/makeCustomResolutionResultFromTypedValue';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {
  GetFormulaWeightQuery as GetFormulaWeightQueryResponse,
  GetFormulaWeightQueryVariables,
} from 'notebook/structured-tables/molecules/__generated/GetFormulaWeightQuery';
import {
  GetVersionedFormulaWeightQuery as GetVersionedFormulaWeightQueryResponse,
  GetVersionedFormulaWeightQueryVariables,
} from 'notebook/structured-tables/molecules/__generated/GetVersionedFormulaWeightQuery';
import {
  GET_FORMULA_WEIGHT_QUERY,
  GET_VERSIONED_FORMULA_WEIGHT_QUERY,
} from 'notebook/structured-tables/molecules/ChemistryQueries';
import {CustomEvalResult} from 'notebook/tables/constants/CustomEvalConstants';
import {
  CustomResolutionQueryType,
  DraftChemicalProperty,
  GetFormulaWeightQuery,
  ServerResolutionResult,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
} from 'notebook/tables/typed-values/TypedValueResolution';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {UnitTypesQuery} from 'units/__generated/UnitTypesQuery';
import {UNIT_TYPES_QUERY} from 'units/UnitsConstants';

function preloadUnitTypes(): void {
  doQuerySafe<UnitTypesQuery>({query: UNIT_TYPES_QUERY}, {}).catch(() => {});
}

/** Formula weight keyed by `${moleculeId}:${versionId}`, for queries that specify a version. */
async function fetchVersionedFormulaWeightByKey(
  queries: Array<GetFormulaWeightQuery & {versionId: string}>
): Promise<Map<string, number | null>> {
  const moleculeVersionSpecifiers = queries.map((query) => ({
    moleculeId: query.moleculeId,
    versionId: query.versionId,
  }));

  const queryResult = await doQueryWithoutCache<
    GetVersionedFormulaWeightQueryResponse,
    GetVersionedFormulaWeightQueryVariables
  >({query: GET_VERSIONED_FORMULA_WEIGHT_QUERY}, {variables: {moleculeVersionSpecifiers}});

  // Order-aligned with moleculeVersionSpecifiers (Promise.all in the resolver preserves order), so
  // zip by position rather than re-deriving a shared key -- a versionId is not enough on its own to
  // disambiguate which specifier a result belongs to when a batch mixes a valid pair with a stale
  // one that happens to reuse the same versionId under a different moleculeId.
  const formulaWeightByKey = new Map<string, number | null>();
  queryResult.moleculeVersions.forEach((moleculeVersion, index) => {
    const {moleculeId, versionId} = moleculeVersionSpecifiers[index];
    const formulaWeight = isInaccessible(moleculeVersion)
      ? null
      : moleculeVersion.chemicalStructure.formulaWeight.__typename === 'FloatPropertyNode'
        ? moleculeVersion.chemicalStructure.formulaWeight.value
        : null;
    formulaWeightByKey.set(`${moleculeId}:${versionId}`, formulaWeight);
  });
  return formulaWeightByKey;
}

/** Formula weight keyed by moleculeId, from the molecule's current version -- the fallback for
 * queries that predate the Molecule Version column (no versionId to pin to). */
async function fetchCurrentVersionFormulaWeightByMoleculeId(
  moleculeIds: Array<string>
): Promise<Map<string, number | null>> {
  const queryResult = await doQueryWithoutCache<
    GetFormulaWeightQueryResponse,
    GetFormulaWeightQueryVariables
  >({query: GET_FORMULA_WEIGHT_QUERY}, {variables: {moleculeIds}});

  const formulaWeightByMoleculeId = new Map<string, number | null>();
  for (const molecule of queryResult.molecules) {
    if (isInaccessible(molecule)) {
      formulaWeightByMoleculeId.set(molecule.invalidId, null);
    } else {
      const formulaWeight = molecule.currentVersion.chemicalStructure.formulaWeight;
      formulaWeightByMoleculeId.set(
        molecule.id,
        formulaWeight.__typename === 'FloatPropertyNode' ? formulaWeight.value : null
      );
    }
  }
  return formulaWeightByMoleculeId;
}

export async function bulkResolveGetFormulaWeightToServerResolutionResult(
  queries: Array<GetFormulaWeightQuery>
): Promise<Array<ServerResolutionResult>> {
  preloadUnitTypes();

  const versionedQueries = queries.filter(
    (query): query is GetFormulaWeightQuery & {versionId: string} => query.versionId != null
  );
  const unversionedMoleculeIds = queries
    .filter((query) => query.versionId == null)
    .map((query) => query.moleculeId);

  const [formulaWeightByKey, formulaWeightByMoleculeId] = await Promise.all([
    versionedQueries.length > 0
      ? fetchVersionedFormulaWeightByKey(versionedQueries)
      : Promise.resolve(new Map<string, number | null>()),
    unversionedMoleculeIds.length > 0
      ? fetchCurrentVersionFormulaWeightByMoleculeId(unversionedMoleculeIds)
      : Promise.resolve(new Map<string, number | null>()),
  ]);

  return queries.map((query) => {
    const formulaWeight =
      query.versionId != null
        ? formulaWeightByKey.get(`${query.moleculeId}:${query.versionId}`)
        : formulaWeightByMoleculeId.get(query.moleculeId);
    if (formulaWeight == null) {
      return makeCustomResolutionResultFromTypedValue(null, 'Could not retrieve formula weight');
    }
    return makeCustomResolutionResultFromTypedValue({
      type: FieldType.FLOAT,
      value: String(formulaWeight),
      rawValue: String(formulaWeight),
      floatValue: formulaWeight,
    });
  });
}

export function getFormulaWeightColumnCustomEval(
  typedValue: TypedValue,
  columnDefinition: ColumnDefinition,
  cellDependencies: Array<TypedValue | null>,
  customEvalOptions: StoichiometryCustomEvalOptions,
  resolveFromResolutionQuery: ResolveQueryFn
): CustomEvalResult {
  const bioentityTypedValue = cellDependencies[0];

  // If there's no bioentity in the dependency, the row may be an unrecognized (draft) molecule:
  // resolve the formula weight directly from its canonical SMILES when present.
  if (bioentityTypedValue == null || bioentityTypedValue.type !== BaseTypedValueType.BIOENTITY) {
    return resolveDraftPropertyFromSmiles(
      typedValue,
      customEvalOptions,
      DraftChemicalProperty.FORMULA_WEIGHT,
      resolveFromResolutionQuery
    );
  }

  const moleculeId = bioentityTypedValue.bioentitySummary.id;

  // The Molecule Version cell (second dependency) is undefined on tables predating that column;
  // resolveFromResolutionQuery falls back to the molecule's current version in that case. It's also
  // absent/unresolved for Lot-derived rows (which never populate this column) or freshly-added rows
  // whose bioentity hasn't resolved yet -- getVersionedBioentityFromMoleculeVersionCell returns null
  // for all of those cases, same as for a legacy plain-string version id.
  const moleculeVersionTypedValue = cellDependencies[1];
  const versionId = getVersionedBioentityFromMoleculeVersionCell(moleculeVersionTypedValue)?.versionApiId;

  const {resolvedValue, error} = resolveFromResolutionQuery(
    typedValue,
    {
      customResolutionQueryType: CustomResolutionQueryType.STOICHIOMETRY_TABLE_GET_FORMULA_WEIGHT,
      moleculeId,
      versionId,
      type: ResolutionQueryType.CUSTOM,
    },
    makeTypedValueFromCustomResolvedValue
  );
  return {val: resolvedValue, error, locked: false};
}
